import MemoryIcon from '@mui/icons-material/Memory';
import { Box, Button, Chip, Stack, Typography, type SxProps } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { trackRapidRobotTemplateCreateClick } from '../../analytics/bpsEvents';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions, electronics, RAPID_ROBOT_COMPLEXITY, type RapidRobotAccent, type RapidRobotComplexity } from '../../utils/constants';
import { getBitsElectronicIcon } from '../../utils/getBitsElectronicIcon';
import { countRequiredElectronicsByType } from '../../utils/requiredElectronicsCounts';
import { Electronic } from '../Dialogs/Robot/RobotTemplate/TemplateCard';

interface RapidRobotCardProps {
  code: string;
  nameKey: string;
  name: string;
  subtitleKey: string;
  subtitle: string;
  description: string;
  descriptionKey: string;
  electronics: Electronic[];
  image: string;
  accent: RapidRobotAccent;
  complexity: RapidRobotComplexity;
}

/** Allow two-line labels (e.g. Build Instructions) to wrap and stay centered in narrow card buttons. */
export const rapidRobotCardButtonTextWrapStyles: SxProps<Theme> = {
  minWidth: 0,
  height: 'auto',
  maxHeight: 'none',
  textAlign: 'center',
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
};

const compactButtonStyles = {
  minHeight: 34,
  px: 1,
  py: 0.5,
  borderRadius: '4px',
  ...rapidRobotCardButtonTextWrapStyles,
  '& .MuiTypography-root': {
    fontSize: '0.75rem',
    lineHeight: 1.2,
    whiteSpace: 'normal',
    textAlign: 'center',
    overflowWrap: 'break-word',
    width: '100%',
  },
};

const lightButtonStyles = {
  minHeight: 42,
  px: 2.75,
  py: 1,
  borderRadius: '8px',
  '& .MuiTypography-root': {
    fontSize: '0.9375rem',
    lineHeight: '26px',
  },
};

const getCreateButtonStyles = (theme: Theme) => ({
  width: '67%',
  '&&': {
    ...compactButtonStyles,
    backgroundColor: 'rapidRobot.createButtonBackground',
    color: 'rapidRobot.createButtonText',
    boxShadow: theme.shadows[2],
    '&:hover': {
      backgroundColor: 'rapidRobot.createButtonHoverBackground',
      color: 'rapidRobot.createButtonText',
      boxShadow: theme.shadows[3],
    },
    ...theme.applyStyles('light', {
      ...lightButtonStyles,
      boxShadow: theme.shadows[2],
      '&:hover': {
        backgroundColor: 'rapidRobot.createButtonHoverBackground',
        color: 'rapidRobot.createButtonText',
        boxShadow: theme.shadows[3],
      },
    }),
  },
});

const RapidRobotCard = ({
  code,
  nameKey,
  name,
  subtitleKey,
  subtitle,
  description,
  descriptionKey,
  electronics: requiredElectronics,
  image,
  accent,
  complexity,
}: RapidRobotCardProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { PROJECT_NAME } = ApplicationActions.Dialogs;

  const complexityLabel =
    complexity === RAPID_ROBOT_COMPLEXITY.MotorsAndSensors
      ? t('rapidRobots.complexity.option.motorsAndSensors', { defaultValue: 'Motors & Sensors' })
      : t('rapidRobots.complexity.option.motors', { defaultValue: 'Motors' });

  const requiredElectronicsWithCounts = countRequiredElectronicsByType(requiredElectronics);

  const getElectronicLabel = (electronicType: string): string => {
    switch (electronicType) {
      case electronics.distanceSensor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.distance-sensor', 'Distance Sensor');
      case electronics.highSpeedMotor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.high-speed-motor', 'High Speed Motor+');
      case electronics.highTorqueMotor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.high-torque-motor', 'High Torque Motor+');
      case electronics.beamBreakSensor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.beam-break-sensor', 'Beam Break Sensor');
      case electronics.magneticSwitch.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.magnetic-switch', 'Magnetic Switch');
      case electronics.BPEadaptor.ELECTRONIC_TYPE:
        return t('electronicExplorer.electronic.BPE-adaptor', 'BPE Adaptor');
      default:
        return electronicType.replace(/_/g, ' ');
    }
  };

  const handleCreate = () => {
    trackRapidRobotTemplateCreateClick(code);
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...PROJECT_NAME,
        options: {
          title: 'projectNameDialog.new.title',
          rapidRobotCode: code,
        },
      })
    );
  };

  return (
    <Stack
      sx={(theme) => {
        const rawAccentColor = theme.palette.rapidRobot[accent];

        return {
          height: '100%',
          minWidth: 0,
          justifyContent: 'space-between',
          borderRadius: '6px',
          padding: { xs: 2, md: 2.5 },
          backgroundColor: 'rapidRobot.cardBackground',
          border: '3px solid',
          borderColor: `rapidRobot.${accent}`,
          boxShadow: `0 0 0 1px ${alpha(rawAccentColor, 0.18)}`,
          transition: 'box-shadow 0.2s',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: `0 0 0 1px ${alpha(rawAccentColor, 0.22)}, ${theme.shadows[1]}`,
          },
        };
      }}
      spacing={2}
    >
      <Box
        sx={{
          width: '100%',
          aspectRatio: '4 / 3',
          borderRadius: '4px',
          overflow: 'hidden',
          backgroundColor: 'spheroWhite.main',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          src={image}
          alt={t('rapidRobots.card.titleWithSubtitle', {
            name: t(nameKey, name),
            subtitle: t(subtitleKey, subtitle),
            defaultValue: '{{name}} - {{subtitle}}',
          })}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 'inherit',
          }}
        />
      </Box>

      <Stack spacing={2} sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 500,
                color: 'rapidRobot.cardText',
                lineHeight: 1,
              }}
            >
              {t(nameKey, name)}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'rapidRobot.cardText' }}>
              {t(subtitleKey, subtitle)}
            </Typography>
          </Stack>
          <Chip
            size="small"
            label={complexityLabel}
            sx={{
              ml: 'auto',
              backgroundColor: `rapidRobot.${accent}`,
              color: 'rapidRobot.pillText',
              borderRadius: '999px',
              fontWeight: 700,
              height: 24,
              '& .MuiChip-label': {
                px: 1.25,
              },
            }}
          />
        </Stack>

        <Typography
          variant="body2"
          sx={{
            color: 'rapidRobot.cardText',
            lineHeight: 1.4,
          }}
        >
          <Trans i18nKey={descriptionKey}>{description}</Trans>
        </Typography>

        <Stack spacing={1}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'rapidRobot.cardText',
            }}
          >
            <Trans i18nKey="rapidRobots.required-electronics">Required Electronics</Trans>
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
            {Object.entries(requiredElectronicsWithCounts).map(([electronicType, count]) => {
              const ElectronicIcon = getBitsElectronicIcon(electronicType);
              const label = getElectronicLabel(electronicType);

              return (
                <Stack key={electronicType} direction="row" alignItems="center" spacing={0.5}>
                  <Chip
                    icon={
                      ElectronicIcon ? (
                        <Box
                          component={ElectronicIcon}
                          aria-hidden
                          sx={{
                            width: 18,
                            height: 18,
                            display: 'block',
                            color: 'rapidRobot.cardText',
                          }}
                        />
                      ) : (
                        <MemoryIcon />
                      )
                    }
                    label={label}
                    size="small"
                    sx={{
                      fontSize: '0.7rem',
                      height: 24,
                      backgroundColor: 'rapidRobot.cardChipBackground',
                      color: 'rapidRobot.cardText',
                      '& .MuiChip-icon': {
                        fontSize: '0.9rem',
                        color: 'rapidRobot.cardText',
                      },
                    }}
                  />
                  {count > 1 ? (
                    <Typography variant="caption" sx={{ color: 'rapidRobot.cardText', fontWeight: 600 }}>
                      {t('rapidRobots.electronics.quantity', { count, defaultValue: 'x{{count}}' })}
                    </Typography>
                  ) : null}
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="row" justifyContent="center" sx={{ paddingTop: 1.5 }}>
        <Button variant="contained" onClick={handleCreate} sx={getCreateButtonStyles}>
          <Typography variant="buttonText">
            <Trans i18nKey="rapidRobots.button.create">Create</Trans>
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );
};

export default RapidRobotCard;
